import React, { useEffect, useMemo, useState } from 'react';
import OpenInNew from '@mui/icons-material/OpenInNew';
import { useParams } from 'react-router-dom';
import Modal from 'components/common/Modal';
// Components
import DataCard from 'components/common/card/DataCard';
import CentsLoader from 'components/common/centsLoader/CentsLoader';
import IndividualValueEditForm from 'components/common/forms/IndividualValueEditForm';
import CreditModalForm from 'components/common/forms/NewCreditModalForm';
import CreditModal from 'components/common/newCreditModal/NewCreditModal';
import { MODAL_THEME } from 'utils/constants';
// APIs
import {
  addToCreditTable,
  deleteIndividualCustomer,
  getBusinessesByCustomer,
  getCreditReasons,
  getIndividualCustomer,
  updateIndividualValueForCustomer,
} from 'api/admin/customers/customers';
import { getLanguages } from 'api/common';

const IndividualCustomerProfile = () => {
  const { id } = useParams();

  const [loading, setLoading] = useState(true);
  const [errorMessage, setErrorMessage] = useState();
  const [languages, setLanguages] = useState([]);
  const [centsCustomer, setCentsCustomer] = useState();
  const [showModal, setShowModal] = useState(false);
  const [modalType, setModalType] = useState();
  const [updatedFormValue, setUpdatedFormValue] = useState(null);
  const [updatedFormKey, setUpdatedFormKey] = useState(null);
  const [newCredit, setNewCredit] = useState('');
  const [showCreditModal, setShowCreditModal] = useState(false);
  const [creditToUpdate, setCreditToUpdate] = useState({});
  const [showNewCreditModal, setShowNewCreditModal] = useState(false);
  const [creditBusinessId, setCreditBusinessId] = useState('');
  const [allBusinessesSimple, setAllBusinessesSimple] = useState([]);
  const [creditReasons, setCreditReasons] = useState([]);
  const [selectedCreditReasonId, setSelectedCreditReasonId] = useState(1);

  const editLanguage = useMemo(() => modalType === 'language', [modalType]);
  const isDebited = useMemo(() => Number(newCredit) < 0, [newCredit]);

  useEffect(() => {
    fetchLanguages();
    fetchIndividualCustomer();
    // eslint-disable-next-line
  }, []);

  const fetchLanguages = async () => {
    try {
      setLoading(true);

      const languages = await getLanguages();
      setLanguages(
        languages?.data?.languages.map((l) => ({
          display: l.language.charAt(0).toUpperCase() + l.language.slice(1),
          value: l.id,
        })),
      );

      setLoading(false);
    } catch (error) {
      setLoading(false);
      setErrorMessage(error?.response?.data?.error);
    }
  };

  /**
   * Retrieve an individual CentsCustomer from our backend
   */
  const fetchIndividualCustomer = async () => {
    try {
      setLoading(true);

      const customerData = await getIndividualCustomer(id);
      const creditReasonsData = await getCreditReasons();
      setCentsCustomer(customerData.data.customer);

      const formattedReasons = creditReasonsData.data.reasons.map((credit) => ({
        display: credit.reason,
        value: credit.id,
      }));

      setCreditReasons(formattedReasons);

      setLoading(false);
    } catch (error) {
      setLoading(false);
      setErrorMessage(error?.response?.data?.error);
    }
  };

  /**
   * Render the core store details
   */
  const renderMainDetails = () => {
    const mainDetails = [
      {
        label: 'First Name',
        rawLabel: 'firstName',
        data: centsCustomer.firstName.trim() || '--',
        editable: true,
      },
      {
        label: 'Last Name',
        rawLabel: 'lastName',
        data: centsCustomer.lastName?.trim() || '--',
        editable: true,
      },
      {
        label: 'Email',
        rawLabel: 'email',
        data: centsCustomer.email || '--',
        editable: true,
      },
      {
        label: 'Phone Number',
        rawLabel: 'phoneNumber',
        data: centsCustomer.phoneNumber,
        editable: true,
      },
      {
        label: 'SMS support',
        rawLabel: 'isSmsSupported',
        data: centsCustomer.isSmsSupported || false,
        editable: true,
        toggleable: true,
      },
      ...(centsCustomer.smsPermanentFailureCode
        ? [
            {
              label: 'Twilio Error',
              type: 'external-link',
              href: `https://www.twilio.com/docs/api/errors/${centsCustomer.smsPermanentFailureCode}`,
              data: (
                <>
                  {centsCustomer.smsPermanentFailureCode}{' '}
                  <OpenInNew fontSize="small" />
                </>
              ),
              editable: false,
            },
          ]
        : []),
    ];

    return (
      <DataCard
        title={'Main Details'}
        cardData={mainDetails}
        onEdit={(modalType) => {
          openEditModal(modalType);
        }}
        onToggle={(updatedFormKey, updatedFormValue) => {
          saveIndividualValueForCustomer(updatedFormKey, updatedFormValue);
          handleCustomerValueChange(null);
        }}
      />
    );
  };

  /**
   * Open the modal type and set various values in state
   *
   * @param {String} modalType
   */
  const openEditModal = (modalType) => {
    setModalType(modalType);
    setUpdatedFormKey(modalType);
    setShowModal(true);
  };

  /**
   * Set the form value to be updated for the Store
   *
   * @param {String} field
   * @param {Any} value
   */
  const handleCustomerValueChange = (value) => {
    const newValue = editLanguage ? value?.value : value;

    setErrorMessage(null);
    setUpdatedFormValue(newValue);
  };

  /**
   * Save a key-value pair to the CentsCustomer model
   *
   * @param {String} field
   * @param {Any} value
   */
  const saveIndividualValueForCustomer = async (field, value) => {
    try {
      setLoading(true);

      const currentField = editLanguage ? 'languageId' : field;

      const data = {
        field: currentField,
        value,
      };

      const centsCustomerData = await updateIndividualValueForCustomer(centsCustomer.id, data);
      setCentsCustomer(centsCustomerData.data.customer);

      setShowModal(false);
      setLoading(false);
    } catch (error) {
      setLoading(false);
      setErrorMessage(error?.response?.data?.error);
    }
  };

  /**
   * Open a modal that edits a given field for a customer
   *
   * Modal should contain:
   *
   * 1) current value for the given modal type
   * 2) options and/or input for updated value
   * 3) save button to update the CentsCustomer
   *
   * @param {String} modalType
   */
  const renderModal = () => {
    const currentValue = centsCustomer[modalType];
    const modalWithDropdown = ['language'];
    const withDropdown = modalWithDropdown.includes(modalType);

    return withDropdown ? (
      <Modal onClose={() => resetModalData()}>
        <IndividualValueEditForm
          modalType={modalType}
          currentValue={currentValue}
          updatedValue={updatedFormValue}
          selectOptions={languages}
          handleValueChange={(event) => handleCustomerValueChange(event)}
          inputType="select"
          onSave={() => {
            saveIndividualValueForCustomer(updatedFormKey, updatedFormValue);
            handleCustomerValueChange(null);
          }}
          error={errorMessage}
          isSubmitDisabled={!updatedFormValue}
          capitalized
        />
      </Modal>
    ) : (
      <Modal onClose={() => resetModalData()}>
        <IndividualValueEditForm
          modalType={modalType}
          currentValue={currentValue}
          updatedValue={updatedFormValue}
          handleValueChange={(event) => handleCustomerValueChange(event)}
          inputType="text"
          onSave={() => {
            saveIndividualValueForCustomer(updatedFormKey, updatedFormValue);
            handleCustomerValueChange(null);
          }}
          error={errorMessage}
          isSubmitDisabled={!updatedFormValue}
          setError={setErrorMessage}
        />
      </Modal>
    );
  };

  const handleCreditUpdate = (credit) => {
    setErrorMessage(null);
    setNewCredit(credit);
  };

  const handleSelectBusinessUpdate = (business) => {
    setCreditBusinessId(business.value);
  };

  /**
   * Set and save the new password for the user
   */
  const saveNewCredit = async () => {
    if (newCredit.length && Number(newCredit)) {
      try {
        setLoading(true);

        const data = {
          creditAmount: Number(newCredit),
          customerId: centsCustomer.id,
          reasonId: !isDebited ? selectedCreditReasonId : null,
        };

        if (modalType === 'createNewCredit') {
          data.businessId = creditBusinessId;
        } else {
          data.businessId = creditToUpdate.businessId;
        }

        const centsCustomerData = await addToCreditTable(centsCustomer.id, data);
        if (centsCustomerData.data && centsCustomerData.data.success) {
          await fetchIndividualCustomer();
          setShowCreditModal(false);
          setLoading(false);
          setShowNewCreditModal(false);
          setCreditToUpdate({});
          setNewCredit('');
          setCreditBusinessId('');
        }

        setLoading(false);
      } catch (error) {
        setLoading(false);
        setErrorMessage(error?.response?.data?.error);
      }
    }
  };

  const handleDeleteCustomer = async () => {
    setLoading(true);
    try {
      await deleteIndividualCustomer(centsCustomer.id);
      await fetchIndividualCustomer();
    } finally {
      setLoading(false);
    }
  };

  const handleCreditReasonChange = (event) => {
    setSelectedCreditReasonId(event.value);
  };

  const resetModalData = () => {
    setShowModal(false);
    setModalType(null);
    setShowCreditModal(false);
    setErrorMessage(null);
    setCreditToUpdate({});
    setNewCredit('');
    setSelectedCreditReasonId(1);
    handleCustomerValueChange(null);
  };

  /**
   * Show the Increase/Decrease credit modal
   */
  const renderCreditModal = () => {
    return (
      <Modal
        onClose={() => {
          resetModalData();
        }}
        modalHeight={MODAL_THEME.height}
      >
        <IndividualValueEditForm
          modalType={modalType}
          currentValue={creditToUpdate.data}
          headerText={`Add or Subtract Credit to ${creditToUpdate.label}`}
          updateValueText={'Negative or Positive amount'}
          updateValueNote={
            'A negative number will subtract from and a positive number will add to the current value displayed above.'
          }
          updatedValue={newCredit}
          creditReasons={creditReasons}
          disableCreditReasons={isDebited}
          selectedCreditReasonId={selectedCreditReasonId}
          handleCreditReasonChange={handleCreditReasonChange}
          handleValueChange={handleCreditUpdate}
          inputType="number"
          onSave={saveNewCredit}
          error={errorMessage}
        />
      </Modal>
    );
  };

  const renderCreditsByBusiness = () => {
    const creditDetails = [];

    centsCustomer.creditsByBusiness.forEach((credit) => {
      creditDetails.push({
        label: credit.businessName,
        rawLabel: 'add orSubtractCredit',
        data: credit.totalCredit,
        editable: true,
        businessId: credit.businessId,
      });
    });

    return (
      <DataCard
        title={'Credits By Business'}
        cardData={creditDetails}
        onEdit={(modalType, cardData) => {
          setModalType(modalType);
          setCreditToUpdate(cardData);
          setShowCreditModal(true);
        }}
      />
    );
  };

  const renderNewCreditModal = () => {
    return (
      <CreditModal
        onClose={() => {
          resetModalData();
          setShowNewCreditModal(false);
        }}
        modalHeight={MODAL_THEME.height}
      >
        <CreditModalForm
          headerText="Add Credit"
          updatedValue={newCredit}
          handleCreditChange={handleCreditUpdate}
          handleSelectBusiness={handleSelectBusinessUpdate}
          updatedCreditBusinessId={creditBusinessId}
          selectOptions={allBusinessesSimple}
          onSave={saveNewCredit}
          creditReasons={creditReasons}
          disableCreditReasons={isDebited}
          selectedCreditReasonId={selectedCreditReasonId}
          handleCreditReasonChange={handleCreditReasonChange}
          error={errorMessage}
        />
      </CreditModal>
    );
  };

  const openNewCreditModal = async (modalType) => {
    const allBusinessesSimpleData = await getBusinessesByCustomer(centsCustomer.id);
    setAllBusinessesSimple(allBusinessesSimpleData.data.businesses);
    setModalType(modalType);
    setShowNewCreditModal(true);
  };

  const renderNewCreditButton = () => {
    return (
      <button
        className="create-new-credit-button"
        onClick={() => {
          openNewCreditModal('createNewCredit');
        }}
      >
        Add New Credit
      </button>
    );
  };

  const renderDeleteCustomerButton = () => {
    return (
      <button className="delete-customer-button" onClick={handleDeleteCustomer}>
        Delete Customer
      </button>
    );
  };

  return (
    <>
      {loading && <CentsLoader />}
      {!loading && (
        <div className="page-container">
          <div className="customer-page-container">
            <div className="customer-page-header-container">
              <h1 className="customer-header">Customer ID: {centsCustomer?.id}</h1>
              <div>
                {centsCustomer ? (
                  <>
                    {renderNewCreditButton()}
                    {renderDeleteCustomerButton()}
                  </>
                ) : null}
              </div>
            </div>
            {centsCustomer ? renderMainDetails() : null}
            {centsCustomer?.creditsByBusiness.length > 0 && renderCreditsByBusiness()}
          </div>
          {showModal && renderModal()}
          {showCreditModal && renderCreditModal()}
          {showNewCreditModal && renderNewCreditModal()}
        </div>
      )}
    </>
  );
};

export default IndividualCustomerProfile;
